/**
 * Storage utility functions for handling different data types in localStorage
 */

/**
 * Get a string value from localStorage
 * @param key - The key to retrieve
 * @param defaultValue - Default value if key doesn't exist
 * @returns The stored string value or defaultValue
 */
export function get(key: string, defaultValue = ''): string {
  try {
    const value = localStorage.getItem(key)
    return value ?? defaultValue
  } catch (error) {
    if (error instanceof Error) {
      console.error(`Error getting string from localStorage: ${error.message}`)
    } else {
      console.error('Error getting string from localStorage:', error)
    }
    return defaultValue
  }
}

/**
 * Get a string value from localStorage
 * @param key - The key to retrieve
 * @param defaultValue - Default value if key doesn't exist
 * @returns The stored string value or defaultValue
 */
export function getString(key: string, defaultValue = ''): string {
  try {
    const value = localStorage.getItem(key)
    return value
      ? (JSON.parse(localStorage.getItem('access_token') ?? '') as string)
      : defaultValue
  } catch (error) {
    if (error instanceof Error) {
      console.error(`Error getting string from localStorage: ${error.message}`)
    } else {
      console.error('Error getting string from localStorage:', error)
    }
    return defaultValue
  }
}

/**
 * Get a number value from localStorage
 * @param key - The key to retrieve
 * @param defaultValue - Default value if key doesn't exist
 * @returns The stored number value or defaultValue
 */
export function getNumber(key: string, defaultValue = 0): number {
  try {
    const value = localStorage.getItem(key)
    if (value === null) return defaultValue
    const parsed = Number(value)
    return isNaN(parsed) ? defaultValue : parsed
  } catch (error) {
    if (error instanceof Error) {
      console.error(`Error getting string from localStorage: ${error.message}`)
    } else {
      console.error('Error getting string from localStorage:', error)
    }
    return defaultValue
  }
}

/**
 * Get an object from localStorage
 * @param key - The key to retrieve
 * @param defaultValue - Default value if key doesn't exist
 * @returns The stored object or defaultValue
 */
export function getObject<T>(key: string, defaultValue: T): T {
  try {
    const value = localStorage.getItem(key)
    if (value === null) return defaultValue
    return JSON.parse(value) as T
  } catch (error) {
    if (error instanceof Error) {
      console.error(`Error getting string from localStorage: ${error.message}`)
    } else {
      console.error('Error getting string from localStorage:', error)
    }
    return defaultValue
  }
}

/**
 * Set a string value in localStorage
 * @param key - The key to store
 * @param value - The string value to store
 */
export function setString(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch (error) {
    if (error instanceof Error) {
      console.error(`Error getting string from localStorage: ${error.message}`)
    } else {
      console.error('Error getting string from localStorage:', error)
    }
  }
}

/**
 * Set a number value in localStorage
 * @param key - The key to store
 * @param value - The number value to store
 */
export function setNumber(key: string, value: number): void {
  try {
    localStorage.setItem(key, value.toString())
  } catch (error) {
    if (error instanceof Error) {
      console.error(`Error getting string from localStorage: ${error.message}`)
    } else {
      console.error('Error getting string from localStorage:', error)
    }
  }
}

/**
 * Set an object in localStorage
 * @param key - The key to store
 * @param value - The object value to store
 */
export function setObject<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    if (error instanceof Error) {
      console.error(`Error getting string from localStorage: ${error.message}`)
    } else {
      console.error('Error getting string from localStorage:', error)
    }
  }
}

/**
 * Remove an item from localStorage
 * @param key - The key to remove
 */
export function removeItem(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    if (error instanceof Error) {
      console.error(`Error getting string from localStorage: ${error.message}`)
    } else {
      console.error('Error getting string from localStorage:', error)
    }
  }
}

/**
 * Clear all items from localStorage
 */
export function clearStorage(): void {
  try {
    localStorage.clear()
  } catch (error) {
    if (error instanceof Error) {
      console.error(`Error getting string from localStorage: ${error.message}`)
    } else {
      console.error('Error getting string from localStorage:', error)
    }
  }
}
